import { ClassNameMap } from "@mui/material";
import { TypographyClasses, TypographyProps } from "@mui/material/Typography";
import isEmpty from "lodash/isEmpty";
import React, { HTMLAttributes } from "react";
import { MuiTypography } from "..";
import { EMPTY_VALUE_DASH } from "../../helpers/constants/common";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isValueEmptyOrDash } from "../../helpers/utils";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";
import { MuiTripleLabelWrapper } from "./MuiTripleLabel.style";

export interface MuiTripleLabelProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | React.ReactNode;
  subValue: string | React.ReactNode;
  labelTypoVariant?: TypographyProps["variant"];
  valueTypoVariant?: TypographyProps["variant"];
  subValueTypoVariant?: TypographyProps["variant"];
  labelClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  valueClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  subValueClasses?: Partial<TypographyClasses> & Partial<ClassNameMap<never>>;
  isValueWithEllipsis?: boolean;
  replaceEmptyWithDash?: boolean;
  labelIcon?: React.ReactNode;
  shouldShowSubValue?: boolean;
}

export const MuiTripleLabel: React.FC<MuiTripleLabelProps> = ({
  label,
  value,
  subValue,
  labelTypoVariant = "h3",
  valueTypoVariant = "h4Bold",
  subValueTypoVariant = "h3",
  labelClasses,
  valueClasses,
  subValueClasses,
  isValueWithEllipsis = false,
  replaceEmptyWithDash = false,
  labelIcon,
  shouldShowSubValue = true,
  ...rest
}) => {
  const shouldShowEllipseLabel = (valueToCheck: string) => {
    const shouldDisplayEllipseLabel =
      isValueWithEllipsis && !isValueEmptyOrDash(valueToCheck);

    let displayValue: string | React.ReactNode;
    if (replaceEmptyWithDash) {
      displayValue = isEmpty(valueToCheck) ? EMPTY_VALUE_DASH : valueToCheck;
    } else {
      displayValue = valueToCheck;
    }

    return { shouldDisplayEllipseLabel, displayValue };
  };

  const renderEllipseLabel = ({
    labelTypoVariant,
    text,
    labelTypoClassName,
  }: {
    labelTypoVariant: TypographyProps["variant"];
    text: string;
    labelTypoClassName: string;
  }) => {
    return (
      <MuiEllipseLabel
        labelTypoVariant={labelTypoVariant}
        text={text}
        labelTypoClassName={labelTypoClassName}
      />
    );
  };

  const renderSubValue = () => {
    const { shouldDisplayEllipseLabel, displayValue } = shouldShowEllipseLabel(
      subValue as string,
    );
    return shouldDisplayEllipseLabel ? (
      renderEllipseLabel({
        text: displayValue as string,
        labelTypoClassName: `sub-value-style max-width ${subValueClasses}`,
        labelTypoVariant: subValueTypoVariant,
      })
    ) : (
      <MuiTypography
        data-testid={COMPONENT_IDS.COMMON_TRIPLE_LABEL_SUB_VALUE}
        component={"h3"}
        className="sub-value-style"
        classes={subValueClasses}
        variant={subValueTypoVariant}
      >
        {displayValue}
      </MuiTypography>
    );
  };

  const renderMainValue = () => {
    const { shouldDisplayEllipseLabel, displayValue } = shouldShowEllipseLabel(
      value as string,
    );
    return shouldDisplayEllipseLabel ? (
      renderEllipseLabel({
        text: displayValue as string,
        labelTypoClassName: `value-style max-width ${valueClasses}`,
        labelTypoVariant: valueTypoVariant,
      })
    ) : (
      <MuiTypography
        data-testid={COMPONENT_IDS.COMMON_TRIPLE_LABEL_MAIN_VALUE}
        component={"h4"}
        className="value-style"
        classes={valueClasses}
        variant={valueTypoVariant}
      >
        {displayValue}
      </MuiTypography>
    );
  };

  return (
    <MuiTripleLabelWrapper {...rest}>
      <MuiTypography
        data-testid={COMPONENT_IDS.COMMON_TRIPLE_LABEL}
        component={"h3"}
        className="label-style"
        classes={labelClasses}
        variant={labelTypoVariant}
      >
        {label} {labelIcon}
      </MuiTypography>

      {renderMainValue()}
      {shouldShowSubValue && renderSubValue()}
    </MuiTripleLabelWrapper>
  );
};
